import { Paper, Stack, Box, Typography, Divider, IconButton } from "@mui/material"
import { motion } from "framer-motion"
import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'

import Logo from "./Logo"

const Footer = () => {
  const techStack = [
    {
      name: 'React',
      color: '#61dafb',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <g fill="none" stroke="#61dafb" strokeWidth="4">
            <ellipse cx="32" cy="32" rx="18" ry="6" transform="rotate(0 32 32)" />
            <ellipse cx="32" cy="32" rx="18" ry="6" transform="rotate(60 32 32)" />
            <ellipse cx="32" cy="32" rx="18" ry="6" transform="rotate(120 32 32)" />
          </g>
          <circle cx="32" cy="32" r="5" fill="#61dafb" />
        </svg>
      )
    },
    {
      name: 'Redux Toolkit',
      color: '#764abc',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <path d="M24 19c2-6 16-6 18 2 1 5-3 9-8 9h-2" fill="none" stroke="#764abc" strokeWidth="4" strokeLinecap="round" />
          <path d="M40 45c-2 6-16 6-18-2-1-5 3-9 8-9h2" fill="none" stroke="#764abc" strokeWidth="4" strokeLinecap="round" />
          <path d="M22 33c-6-2-6-16 2-18 5-1 9 3 9 8v2" fill="none" stroke="#764abc" strokeWidth="4" strokeLinecap="round" />
          <circle cx="22" cy="32" r="4" fill="#764abc" />
          <circle cx="32" cy="22" r="4" fill="#764abc" />
          <circle cx="42" cy="32" r="4" fill="#764abc" />
        </svg>
      )
    },
    {
      name: 'Material UI',
      color: '#00b0ff',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <path d="M8 16l24 14 24-14v12L32 42 8 28z" fill="#00b0ff"/>
          <path d="M8 32l24 14 24-14v12L32 54 8 44z" fill="#0081cb"/>
        </svg>
      )
    },
    {
      name: 'Supabase',
      color: '#3ecf8e',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <path d="M32 6l20 28H34l8 24-30-28h18z" fill="#3ecf8e" stroke="#2ea46c" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      name: 'MongoDB',
      color: '#4db33d',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <path d="M32 6s14 12 14 28-14 24-14 24-14-8-14-24S32 6 32 6z" fill="#4db33d" />
          <path d="M32 14v36" stroke="#26631f" strokeWidth="4" strokeLinecap="round" />
        </svg>
      )
    },
    {
      name: 'Node.js',
      color: '#8cc84b',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <path d="M32 6l24 14v24L32 58 8 44V20z" fill="#303030" stroke="#8cc84b" strokeWidth="3" />
          <text x="32" y="39" textAnchor="middle" fontSize="18" fontWeight="700" fill="#8cc84b">JS</text>
        </svg>
      )
    },
    {
      name: 'Express.js',
      color: '#f5f5f5',
      icon: (
        <svg viewBox="0 0 64 64" width="36" height="36">
          <rect x="8" y="14" width="48" height="36" rx="8" fill="#1a1a1a" />
          <text x="32" y="38" textAnchor="middle" fontSize="18" fontWeight="600" fill="#f5f5f5">Ex</text>
        </svg>
      )
    }
  ]

  return (
    <Paper square={true} sx={{ backgroundImage: "unset", padding: { xs: "2.5rem 1.5rem", md: "3.5rem" }, position: 'relative', overflow: 'hidden' }}>
      {/* Logo positioned in specified area - smaller size */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        mb: 3,
        mt: 1
      }}>
        <Box sx={{ 
          maxWidth: { xs: "120px", sm: "140px", md: "160px" },
          width: "auto"
        }}>
          <Logo variant="footer" />
        </Box>
      </Box>
      
      <Divider sx={{ my: 3 }} />

      {/* Tech stack ribbon */}
      <Box sx={{ position: 'relative', mb: 6 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              textAlign: 'center'
            }}
          >
            Technologies & Libraries We Use
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 720,
              textAlign: 'center',
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: 1.7
            }}
          >
            Building ZETFLIX was a journey of retries, late nights, and lessons learned. I almost quit more than once, but every setback unlocked new skills and a deeper love for creating. This stack is the toolkit that kept the vision alive.
          </Typography>
        </Stack>

        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, transparent 20%, transparent 80%, rgba(255,255,255,0.04) 100%)',
            maskImage: 'radial-gradient(circle at 0% 50%, black 0%, transparent 70%)',
            opacity: 0.4,
            pointerEvents: 'none'
          }}
        />

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          gap={{ xs: 2, sm: 2.5, md: 3 }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          {techStack.map((tech, index) => (
            <Box
              key={tech.name}
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.4 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ scale: 1.12, y: -4, filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.45))' }}
              sx={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 3,
                padding: { xs: '0.75rem 1rem', md: '1rem 1.25rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: { xs: 120, md: 140 },
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {tech.icon}
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: tech.color,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  fontSize: { xs: '0.72rem', md: '0.8rem' },
                  textTransform: 'uppercase'
                }}
              >
                {tech.name}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Legal Section - Center */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontWeight: "bold", 
            mb: 2,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            letterSpacing: "0.5px",
            fontSize: "0.95rem"
          }}
        >
          LEGAL
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            maxWidth: "600px",
            margin: "0 auto",
            fontFamily: "'Inter', 'Roboto', sans-serif",
            lineHeight: 1.6,
            fontSize: "0.875rem"
          }}
        >
          This platform serves as a content aggregator and does not host any media files directly. All content is streamed through trusted third-party services while maintaining the highest standards of user privacy and security.
        </Typography>
      </Box>
      
      {/* Contact Section - Center */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontWeight: "bold",
            mb: 2,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            letterSpacing: "0.5px",
            fontSize: "0.95rem"
          }}
        >
          CONTACT US
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          <IconButton
            onClick={() => window.open('mailto:zeticuz.samxerz@gmail.com', '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <EmailIcon />
          </IconButton>
          <IconButton
            onClick={() => window.open('https://www.facebook.com/people/Zetflix/61578123735793/', '_blank')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: '#1877F2',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <FacebookIcon />
          </IconButton>
        </Stack>
      </Box>
      
      {/* Copyright - Bottom Center */}
      <Box sx={{ textAlign: "center", pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontFamily: "'Inter', 'Roboto', sans-serif",
            fontWeight: "500",
            fontSize: "0.875rem",
            letterSpacing: "0.3px"
          }}
        >
          © 2025 ZETICUZ. All rights reserved.
        </Typography>
      </Box>
    </Paper>
  )
}

export default Footer
